/**
 * Navigation utilities for Capacitor iOS app
 * 
 * - Internal routes: Use React Router's navigate()
 * - External URLs: Use Capacitor Browser plugin
 */

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

/**
 * Open an external URL using the appropriate method
 * - Native app: Uses Capacitor Browser (in-app browser)
 * - Web: Falls back to window.open
 * 
 * @param {string} url - The external URL to open
 * @param {object} options - Optional settings
 * @param {string} options.windowName - For web fallback (default: '_blank')
 * @param {string} options.windowFeatures - For web fallback (default: '')
 */
export const openExternalUrl = async (url, options = {}) => {
  const { windowName = '_blank', windowFeatures = '' } = options;
  
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url });
    } catch (error) {
      console.error('Failed to open URL with Capacitor Browser:', error);
      // Fallback to window.open if Browser fails
      window.open(url, windowName, windowFeatures);
    }
  } else {
    // Web environment - use window.open
    window.open(url, windowName, windowFeatures);
  }
};

/**
 * Open a social share URL
 * Optimized for sharing on Facebook, Twitter, WhatsApp, etc.
 * 
 * @param {string} url - The share URL
 */
export const openShareUrl = async (url) => {
  await openExternalUrl(url, { 
    windowName: '_blank', 
    windowFeatures: 'width=600,height=400' 
  });
};

/**
 * Open Stripe dashboard or onboarding URL
 * 
 * @param {string} url - The Stripe URL
 */
export const openStripeUrl = async (url) => {
  await openExternalUrl(url);
};

/**
 * Check if running in native Capacitor environment
 */
export const isNativeApp = () => Capacitor.isNativePlatform();
